import { useState } from "react";
import styled from "styled-components";
import AssistantChatPreview from "../components/AssistantChatPreview";
import { ClaudeLogo, ChatGPTLogo } from "../components/AssistantLogos";

export const MCP_SERVER_URL = "https://mcp.biterecipes.co/mcp";
// Bite's listing in Claude's connector directory (slug "bite").
const CLAUDE_DIRECTORY_URL = "https://claude.ai/directory/connectors/bite";
// Bite's listing in ChatGPT's plugin directory (listed as "Bite Recipes").
const CHATGPT_DIRECTORY_URL =
  "https://chatgpt.com/plugins/plugin_asdk_app_6ad8c76d3f9c81918b67b2dcd151f364";
const CONNECTED_APPS_URL = "/app/settings/connected-apps";
const SUPPORT_EMAIL = "support@biterecipes.co";

const examplePrompts = [
  {
    label: "Plan the week",
    prompt:
      "Plan my dinners Mon–Fri from my favorites, nothing over 45 minutes.",
    result:
      "It looks through the recipes you’ve favorited, picks five that fit, and puts them straight into your Bite meal plan.",
  },
  {
    label: "Cook what you have",
    prompt:
      "What can I make with chicken, rice and spinach from my saved recipes?",
    result:
      "It searches your own library first, so the answer is a recipe you already loved enough to save.",
  },
  {
    label: "Find a recipe",
    prompt: "Find that lemon pasta I saved last summer.",
    result:
      "Bite searches your library and brings it right back, even if you forgot what you called it.",
  },
  {
    label: "Save from chat",
    prompt: "Save this recipe to Bite.",
    result:
      "Never lose a recipe from a chat. Whether it came up with it, you pasted it or you snapped a cookbook page, it lands in your Bite library, private to you.",
  },
  {
    label: "Fix a recipe",
    prompt: "Make my chili recipe dairy-free.",
    result:
      "It updates the recipe in Bite, like swapping the cream and cheese. It only edits recipes you created.",
  },
  {
    label: "Start a collection",
    prompt: "Make me a Thanksgiving collection with these.",
    result:
      "It creates a private collection in Bite and adds the recipes, ready when you open the app.",
  },
  {
    label: "Discover on Bite",
    prompt: "Find a popular banana bread on Bite.",
    result:
      "It searches public recipes from the whole Bite community, most loved first, and can save one for you.",
  },
  {
    label: "Import a link",
    prompt: "Save the recipe from this TikTok.",
    result:
      "Paste a link from a food blog, Instagram or TikTok and Bite turns it into a clean recipe you can cook from.",
  },
  {
    label: "Shop for it",
    prompt: "Make me a shopping list for this week.",
    result:
      "Bite builds the list from your meal plan, ready to take to your grocery app.",
  },
];

const claudeSteps = [
  <>
    Open <strong>Bite in Claude’s connector directory</strong> with the button
    below (or search for Bite in Claude under{" "}
    <strong>Customize → Connectors</strong>).
  </>,
  <>
    Choose <strong>Connect</strong>.
  </>,
  <>
    Sign in to Bite when asked and choose <strong>Approve</strong>.
  </>,
  <>
    In a chat, tap <strong>+</strong> → <strong>Connectors</strong> and make
    sure Bite is on. Then just ask.
  </>,
];

const chatGptSteps = [
  <>
    Open <strong>Bite Recipes in ChatGPT’s plugin directory</strong> with the
    button below (or search for Bite Recipes in ChatGPT under{" "}
    <strong>Plugins</strong>).
  </>,
  <>
    Choose <strong>Install plugin</strong>.
  </>,
  <>
    Sign in to Bite when asked and choose <strong>Approve</strong>.
  </>,
  <>
    Start a new chat, type <strong>@Bite Recipes</strong> and ask away.
  </>,
];

const canSee = [
  "Recipes you’ve created, saved, favorited or cooked",
  "Collections you own or that friends shared with you",
  "Your meal plan and shopping list",
  "Your private recipe notes",
  "Public recipes from the Bite community",
];

const cantSee = [
  "Your email address or password",
  "Your payment details",
  "Other people’s private recipes",
];

const promises = [
  {
    title: "Private by default",
    body: "Recipes saved from chat are private. Only you see them until you choose to share.",
  },
  {
    title: "A photo makes it yours",
    body: "If a recipe arrives without an approved photo, it’s saved as a draft. Add a photo in Bite whenever you’re ready.",
  },
  {
    title: "Photos are checked",
    body: "Photos that come from a link go through the same safety check as photos you upload in the app.",
  },
  {
    title: "Nothing gets deleted",
    body: "Your assistant can’t delete anything or make a private recipe public, and it never posts reviews or follows people. It only edits recipes you created.",
  },
  {
    title: "Same as in the app",
    body: "Marking a recipe as cooked lets its creator know, just like it does in Bite.",
  },
  {
    title: "Only what’s needed",
    body: "We keep a 90-day record of which Bite actions were used, never the recipes or what you said in chat. If you ask your assistant to report a problem, we get that report.",
  },
];

function CopyAddress() {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(MCP_SERVER_URL);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      setCopied(false);
    }
  };

  return (
    <AddressBox>
      <code>{MCP_SERVER_URL}</code>
      <CopyButton type="button" onClick={copy} aria-live="polite">
        {copied ? "Copied" : "Copy"}
      </CopyButton>
    </AddressBox>
  );
}

function Connect() {

  return (
    <Page>
      <main>
        <Hero aria-labelledby="connect-heading">
          <HeroCopy>
            <Eyebrow>Bite for Claude &amp; ChatGPT</Eyebrow>
            <h1 id="connect-heading">
              Your recipes,
              <br />
              in every chat.
            </h1>
            <p>
              Connect Bite to Claude or ChatGPT and talk through dinner the way
              you would with a friend who knows every recipe you’ve ever saved.
              Plan the week, build the shopping list, save that recipe you
              just found.
            </p>
            <HeroActions>
              <PrimaryButton
                href={CLAUDE_DIRECTORY_URL}
                target="_blank"
                rel="noreferrer"
              >
                Connect Claude
              </PrimaryButton>
              <SecondaryButton
                href={CHATGPT_DIRECTORY_URL}
                target="_blank"
                rel="noreferrer"
              >
                Connect ChatGPT
              </SecondaryButton>
            </HeroActions>
            <small>Your recipes stay private.</small>
          </HeroCopy>

          <AssistantChatPreview />
        </Hero>

        <Section id="examples" aria-labelledby="examples-heading">
          <SectionHeader>
            <Eyebrow>In Claude or ChatGPT</Eyebrow>
            <h2 id="examples-heading">
              What’s for dinner?
              <br />
              Just ask.
            </h2>
            <p>
              Your assistant works from your own Bite library, so the answers
              are recipes you already saved.
            </p>
          </SectionHeader>
          <PromptGrid>
            {examplePrompts.map(example => (
              <PromptCard key={example.label}>
                <small>{example.label}</small>
                <blockquote>“{example.prompt}”</blockquote>
                <p>{example.result}</p>
              </PromptCard>
            ))}
          </PromptGrid>
        </Section>

        <Section id="connect" aria-labelledby="connect-steps-heading">
          <SectionHeader>
            <Eyebrow>Get connected</Eyebrow>
            <h2 id="connect-steps-heading">Two minutes, one time.</h2>
            <p>
              You’ll need a Bite account. Claude and ChatGPT both connect
              straight from their directories; other apps can use the Bite
              address below. Menu names can
              change a little as Claude and ChatGPT update their apps, and
              connector options depend on your Claude or ChatGPT plan.
            </p>
            <CopyAddress />
          </SectionHeader>
          <StepsGrid>
            <StepsCard id="claude" aria-labelledby="claude-heading">
              <h3 id="claude-heading">Claude</h3>
              <ol>
                {claudeSteps.map((step, index) => (
                  <li key={index}>
                    <span>{step}</span>
                  </li>
                ))}
              </ol>
              <ClaudeButton
                href={CLAUDE_DIRECTORY_URL}
                target="_blank"
                rel="noreferrer"
              >
                <ClaudeLogo size={18} />
                Connect to Claude
              </ClaudeButton>
            </StepsCard>
            <StepsCard id="chatgpt" aria-labelledby="chatgpt-heading">
              <h3 id="chatgpt-heading">ChatGPT</h3>
              <ol>
                {chatGptSteps.map((step, index) => (
                  <li key={index}>
                    <span>{step}</span>
                  </li>
                ))}
              </ol>
              <ChatGPTButton
                href={CHATGPT_DIRECTORY_URL}
                target="_blank"
                rel="noreferrer"
              >
                <ChatGPTLogo size={18} />
                Connect to ChatGPT
              </ChatGPTButton>
            </StepsCard>
          </StepsGrid>
        </Section>

        <Section id="privacy" aria-labelledby="privacy-heading">
          <SectionHeader>
            <Eyebrow>Your kitchen, your rules</Eyebrow>
            <h2 id="privacy-heading">What your assistant can see.</h2>
            <p>
              You choose to connect, and you can disconnect at any time.
              Here’s exactly what that connection reaches.
            </p>
          </SectionHeader>
          <AccessGrid>
            <AccessCard>
              <h3>It can see and help with</h3>
              <ul>
                {canSee.map(item => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </AccessCard>
            <AccessCard $muted>
              <h3>It never sees</h3>
              <ul>
                {cantSee.map(item => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </AccessCard>
          </AccessGrid>
          <PromiseGrid>
            {promises.map(item => (
              <li key={item.title}>
                <strong>{item.title}</strong>
                <span>{item.body}</span>
              </li>
            ))}
          </PromiseGrid>
          <FinePrint>
            What you share in a chat is handled by Claude or ChatGPT under
            their own terms and privacy policies. Read more in our{" "}
            <a href="/privacy">Privacy Policy</a> and{" "}
            <a href="/terms">Terms of Service</a>.
          </FinePrint>
        </Section>

        <Section id="disconnect" aria-labelledby="disconnect-heading">
          <DisconnectCard>
            <div>
              <Eyebrow>Changed your mind?</Eyebrow>
              <h2 id="disconnect-heading">Disconnect anytime.</h2>
              <p>
                Sign in to Bite on the web and open{" "}
                <strong>Settings → Connected apps</strong>. Disconnecting
                works right away, and your assistant can’t reach Bite again
                until you reconnect. You can also remove Bite from Claude or
                ChatGPT’s own connector settings. If you delete your Bite
                account, every connection goes with it.
              </p>
            </div>
            <SecondaryButton href={CONNECTED_APPS_URL}>
              Open Connected apps
            </SecondaryButton>
          </DisconnectCard>
          <FinePrint>
            Questions or something not working? Email us at{" "}
            <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
          </FinePrint>
        </Section>
      </main>
      <Footer>
        <FooterLinks aria-label="Footer navigation">
          <a href="/">Bite</a>
          <a href="/#features">Features</a>
          <a href={`mailto:${SUPPORT_EMAIL}`}>Contact</a>
          <a href="/privacy">Privacy</a>
          <a href="/terms">Terms</a>
        </FooterLinks>
      </Footer>
    </Page>
  );
}

const Page = styled.div`
  background: #ffffff;
  color: #080808;
  overflow: hidden;

  h1,
  h2,
  h3,
  p {
    margin: 0;
  }

  h1 {
    font-family: "Recoleta", Georgia, serif;
    font-size: clamp(3.2rem, 6.4vw, 5.8rem);
    line-height: 0.98;
    letter-spacing: 0;
    font-weight: 500;
  }

  h2 {
    font-family: "Recoleta", Georgia, serif;
    font-size: clamp(2.3rem, 4.2vw, 3.8rem);
    line-height: 1.02;
    letter-spacing: 0;
    font-weight: 500;
  }

  h3 {
    font-family: "Recoleta", Georgia, serif;
    font-size: 1.7rem;
    font-weight: 500;
  }

  p {
    color: #687080;
    font-size: clamp(1.05rem, 1.5vw, 1.2rem);
    line-height: 1.45;
  }

  a {
    color: inherit;
  }

  @media (max-width: 720px) {
    h1 {
      font-size: clamp(2.8rem, 13vw, 3.8rem);
    }

    h2 {
      font-size: clamp(2.1rem, 9vw, 2.9rem);
    }
  }
`;

const Eyebrow = styled.span`
  color: #7d8490;
  font-size: 0.82rem;
  font-weight: 750;
  letter-spacing: 0.12em;
  text-transform: uppercase;
`;

// Like the home page, each section fills the screen so the next one never
// peeks in. The hero fills what's left below the sticky nav (68px + 16px).
const Hero = styled.section`
  width: min(100% - 40px, 1180px);
  margin: 0 auto;
  min-height: calc(100vh - 84px);
  min-height: calc(100svh - 84px);
  padding: 72px 0 96px;
  box-sizing: border-box;
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 470px);
  gap: 64px;
  align-items: center;
  align-content: center;

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
    gap: 48px;
    padding: 48px 0 72px;
  }

  @media (max-width: 720px) {
    min-height: calc(100vh - 70px);
    min-height: calc(100svh - 70px);
  }
`;

const HeroCopy = styled.div`
  display: grid;
  gap: 24px;
  max-width: 580px;

  > small {
    color: #6d7480;
    font-size: 0.95rem;
  }
`;

const HeroActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
`;

const buttonBase = `
  min-height: 48px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 24px;
  border-radius: 999px;
  font-size: 1rem;
  font-weight: 650;
  text-decoration: none;
  transition: transform 180ms ease, background-color 180ms ease;
  box-sizing: border-box;

  &:hover {
    transform: translateY(-1px);
  }

  &:focus-visible {
    outline: 3px solid #8ab4ff;
    outline-offset: 4px;
  }
`;

const PrimaryButton = styled.a`
  ${buttonBase}
  background: #050505;
  color: #ffffff !important;

  &:hover {
    background: #242424;
  }
`;

// Claude's brand coral with white text, as Claude's own buttons use.
const ClaudeButton = styled.a`
  ${buttonBase}
  justify-self: start;
  gap: 10px;
  background: #d97757;
  color: #ffffff !important;

  &:hover {
    background: #c96442;
  }
`;

// ChatGPT's own black button with white text.
const ChatGPTButton = styled.a`
  ${buttonBase}
  justify-self: start;
  gap: 10px;
  background: #000000;
  color: #ffffff !important;

  &:hover {
    background: #2f2f2f;
  }
`;

const SecondaryButton = styled.a`
  ${buttonBase}
  background: #ffffff;
  color: #080808;
  border: 1px solid #dfe2e6;

  &:hover {
    background: #f6f7f8;
  }
`;

const Section = styled.section`
  width: min(100% - 40px, 1120px);
  margin: 0 auto;
  min-height: 100vh;
  min-height: 100svh;
  padding: 96px 0;
  box-sizing: border-box;
  display: grid;
  align-content: center;
  gap: 40px;

  @media (max-width: 720px) {
    padding: 84px 0;
    gap: 32px;
  }
`;

const SectionHeader = styled.div`
  display: grid;
  gap: 18px;
  max-width: 680px;
`;

const PromptGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;

  @media (max-width: 980px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const PromptCard = styled.article`
  padding: 24px 22px;
  border: 1px solid #eeeeee;
  border-radius: 18px;
  background: #ffffff;
  box-shadow: 0 2px 10px rgba(15, 23, 42, 0.03);
  display: grid;
  gap: 12px;
  align-content: start;

  small {
    color: #6c8e7d;
    font-size: 0.78rem;
    font-weight: 750;
    letter-spacing: 0.1em;
    text-transform: uppercase;
  }

  blockquote {
    margin: 0;
    color: #12151a;
    font-family: "Recoleta", Georgia, serif;
    font-size: 1.3rem;
    line-height: 1.25;
    overflow-wrap: anywhere;
  }

  p {
    font-size: 0.98rem;
  }
`;

const AddressBox = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 8px 8px 18px;
  border: 1px solid #dfe2e6;
  border-radius: 999px;
  background: #f7f8fa;
  max-width: 460px;
  box-sizing: border-box;

  code {
    flex: 1;
    min-width: 0;
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 0.95rem;
    color: #12151a;
    overflow-wrap: anywhere;
  }

  @media (max-width: 420px) {
    padding-left: 14px;

    code {
      font-size: 0.8rem;
    }
  }
`;

const CopyButton = styled.button`
  min-height: 38px;
  padding: 0 18px;
  border: 0;
  border-radius: 999px;
  background: #050505;
  color: #ffffff;
  font-size: 0.92rem;
  font-weight: 650;
  cursor: pointer;

  &:focus-visible {
    outline: 3px solid #8ab4ff;
    outline-offset: 3px;
  }
`;

const StepsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 20px;

  @media (max-width: 820px) {
    grid-template-columns: 1fr;
  }
`;

const StepsCard = styled.section`
  padding: 30px 28px;
  border: 1px solid #eeeeee;
  border-radius: 20px;
  background: #ffffff;
  scroll-margin-top: 110px;
  display: grid;
  /* Title, steps, button: the steps take the spare height so both cards'
     buttons line up at the bottom. */
  grid-template-rows: auto 1fr auto;
  gap: 18px;

  ol {
    margin: 0;
    padding: 0;
    list-style: none;
    counter-reset: step;
    display: grid;
    align-content: start;
    gap: 16px;
  }

  li {
    counter-increment: step;
    display: grid;
    grid-template-columns: 30px minmax(0, 1fr);
    gap: 12px;
    color: #3a404a;
    font-size: 1rem;
    line-height: 1.45;
  }

  li::before {
    content: counter(step);
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: #f0f3f1;
    color: #6c8e7d;
    font-weight: 750;
    font-size: 0.88rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  strong {
    color: #12151a;
    font-weight: 650;
  }

  @media (max-width: 590px) {
    padding: 24px 20px;
  }
`;

const AccessGrid = styled.div`
  display: grid;
  grid-template-columns: 1.4fr 1fr;
  gap: 16px;

  @media (max-width: 820px) {
    grid-template-columns: 1fr;
  }
`;

const AccessCard = styled.div`
  padding: 28px;
  border-radius: 20px;
  background: ${props => (props.$muted ? "#f7f8fa" : "#eef3f0")};
  display: grid;
  gap: 16px;
  align-content: start;

  ul {
    margin: 0;
    padding: 0;
    list-style: none;
    display: grid;
    gap: 10px;
  }

  li {
    position: relative;
    padding-left: 26px;
    color: #2a2f36;
    font-size: 1rem;
    line-height: 1.4;
  }

  li::before {
    content: "${props => (props.$muted ? "✕" : "✓")}";
    position: absolute;
    left: 0;
    top: 0;
    color: ${props => (props.$muted ? "#9aa0a8" : "#6c8e7d")};
    font-weight: 750;
  }

  @media (max-width: 590px) {
    padding: 22px 20px;
  }
`;

const PromiseGrid = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0 32px;

  li {
    padding: 20px 0;
    border-top: 1px solid #eceff2;
    display: grid;
    gap: 6px;
  }

  strong {
    color: #1c2128;
    font-size: 1.05rem;
    font-weight: 650;
  }

  span {
    color: #7d8490;
    font-size: 0.98rem;
    line-height: 1.45;
  }

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 590px) {
    grid-template-columns: 1fr;
  }
`;

const FinePrint = styled.p`
  && {
    font-size: 0.95rem;
    color: #7d8490;
  }

  a {
    color: #12151a;
    font-weight: 600;
  }
`;

const DisconnectCard = styled.div`
  padding: 36px 32px;
  border: 1px solid #eeeeee;
  border-radius: 24px;
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 32px;
  align-items: center;

  > div {
    display: grid;
    gap: 14px;
    max-width: 640px;
  }

  @media (max-width: 820px) {
    grid-template-columns: 1fr;
    padding: 28px 22px;
  }
`;

const Footer = styled.footer`
  width: min(100% - 40px, 1120px);
  margin: 0 auto;
  padding: 36px 0 46px;
  display: flex;
  justify-content: center;
`;

const FooterLinks = styled.nav`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 18px 28px;

  a {
    color: #596171;
    font-size: 0.95rem;
    font-weight: 600;
    text-decoration: none;
  }

  a:hover {
    color: #080808;
  }
`;

export default Connect;
